import React from 'react';
import withTranslation from 'hoc/withTranslation';
import { SparkleIcon } from 'icons/common/Sparkle';
import { TriangleAlertIcon } from 'icons/common/TriangleAlert';
import { CircleCheckIcon } from 'icons/common/CircleCheck';
import { CheckIcon } from 'icons/common/Check';
import { SendIcon } from 'icons/common/Send';
import { RotateCcwIcon } from 'icons/common/RotateCcw';
import { BotIcon } from 'icons/common/Bot';
import { PaperclipIcon } from 'icons/common/Paperclip';
import { XIcon } from 'icons/common/X';
import useRoutingAIChat from './useRoutingAIChat';
import { AgentTracePanel } from './AgentTracePanel';
import {
  StyledHeaderIcon,
  StyledPanel,
  StyledDropOverlay,
  StyledHeader,
  StyledHeaderLeft,
  StyledHeaderTitle,
  StyledHeaderCopy,
  StyledHeaderText,
  StyledHeaderBadge,
  StyledCloseButton,
  StyledHeaderActions,
  StyledBody,
  StyledConversation,
  StyledThread,
  StyledPromptBanner,
  StyledResponseCard,
  StyledResponseLabel,
  StyledResponseIcon,
  StyledResponseText,
  StyledUploadProgressTrack,
  StyledUploadProgressFill,
  StyledResponseActions,
  StyledCardButton,
  StyledSelectedActionIcon,
  StyledInputRow,
  StyledUploadButton,
  StyledInput,
  StyledSendButton,
  StyledTypingIndicator,
  StyledHiddenInput,
  StyledRestartButton,
} from './RoutingAIChatStyled';
import {
  THREAD_ITEM_TYPES,
  RESPONSE_VARIANTS,
  type RoutingAIChatProps,
  type ResponseThreadItem,
} from './RoutingAIChatTypes';


const RoutingAIChat = ({
  translations: { routingAiChat, units },
}: RoutingAIChatProps) => {
  const {
    showPanel,
    draft,
    setDraft,
    isDraggingOver,
    isStreaming,
    isWorkspaceConnecting,
    isWorkspaceReady,
    threadItems,
    selectedActionsByMessage,
    welcomeCard,
    bottomRef,
    panelRef,
    fileInputRef,
    handleClose,
    handleRestart,
    handleCardAction,
    handleSubmit,
    handleUploadClick,
    handleFileChange,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useRoutingAIChat({ routingAiChat, units });

  const isInteractionBlocked = isStreaming || isWorkspaceConnecting || !isWorkspaceReady;

  return (
    <>
      {showPanel && (
        <StyledPanel
          ref={panelRef}
          data-testid="routing-ai-chat-panel"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDraggingOver && (
            <StyledDropOverlay>{routingAiChat.uploadCsv.dropPrompt}</StyledDropOverlay>
          )}
          <StyledHiddenInput
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileChange}
          />
          <StyledHeader>
            <StyledHeaderLeft>
              <StyledHeaderTitle>
                <StyledHeaderIcon>
                  <BotIcon size={15} color="currentColor" />
                </StyledHeaderIcon>
                <StyledHeaderCopy>
                  <StyledHeaderText>{routingAiChat.title}</StyledHeaderText>
                  <StyledHeaderBadge>{routingAiChat.fleetBadge}</StyledHeaderBadge>
                </StyledHeaderCopy>
              </StyledHeaderTitle>
            </StyledHeaderLeft>
            <StyledHeaderActions>
              <StyledRestartButton
                type="button"
                onClick={handleRestart}
                disabled={isStreaming || isWorkspaceConnecting}
                aria-label={routingAiChat.restartLabel}
                title={routingAiChat.restartLabel}
              >
                <RotateCcwIcon size={15} color="currentColor" />
              </StyledRestartButton>
              <StyledCloseButton
                type="button"
                onClick={handleClose}
                aria-label={routingAiChat.closeLabel}
              >
                <XIcon size={16} color="currentColor" />
              </StyledCloseButton>
            </StyledHeaderActions>
          </StyledHeader>
          <StyledBody>
            <StyledConversation>
              <StyledThread>
                {[welcomeCard, ...threadItems].map((item) => {
                  if (item.type === THREAD_ITEM_TYPES.PROMPT) {
                    return <StyledPromptBanner key={item.id}>{item.text}</StyledPromptBanner>;
                  }

                  const selectedActionKey = selectedActionsByMessage[String(item.id)];
                  const responseItem = item as ResponseThreadItem;

                  return (
                    <StyledResponseCard key={item.id} variant={item.variant}>
                      {item.label && (
                        <StyledResponseLabel>
                          <StyledResponseIcon variant={item.variant}>
                            {item.variant === RESPONSE_VARIANTS.WARNING ? (
                              <TriangleAlertIcon size={14} color="currentColor" />
                            ) : item.variant === RESPONSE_VARIANTS.READY ? (
                              <CircleCheckIcon size={14} color="currentColor" />
                            ) : (
                              <SparkleIcon size={14} color="currentColor" />
                            )}
                          </StyledResponseIcon>
                          {item.label}
                        </StyledResponseLabel>
                      )}
                      <StyledResponseText>{item.body}</StyledResponseText>
                      {typeof responseItem.progress === 'number' && responseItem.progress < 100 && (
                        <StyledUploadProgressTrack
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={responseItem.progress}
                          aria-label="Upload progress"
                        >
                          <StyledUploadProgressFill progress={responseItem.progress} />
                        </StyledUploadProgressTrack>
                      )}
                      {responseItem.streaming &&
                        !item.body &&
                        !responseItem.traceSteps?.length && (
                          <StyledTypingIndicator>
                            <span />
                            <span />
                            <span />
                          </StyledTypingIndicator>
                        )}
                      {(responseItem.traceSteps?.length ?? 0) > 0 && (
                        <AgentTracePanel
                          steps={responseItem.traceSteps!}
                          streaming={!!responseItem.streaming}
                          labels={{
                            agentTrace: routingAiChat.agentTrace,
                            agentTraceRunning: routingAiChat.agentTraceRunning,
                          }}
                        />
                      )}
                      {item.actions?.length > 0 && (
                        <StyledResponseActions>
                          {item.actions.map((action) => (
                            <StyledCardButton
                              key={`${item.id}-${action.key}`}
                              type="button"
                              secondary={action.secondary}
                              selected={selectedActionKey === action.key}
                              disabled={Boolean(selectedActionKey)}
                              aria-pressed={selectedActionKey === action.key}
                              onClick={() => handleCardAction(item.id, action)}
                            >
                              {selectedActionKey === action.key && (
                                <StyledSelectedActionIcon>
                                  <CheckIcon size={12} color="currentColor" />
                                </StyledSelectedActionIcon>
                              )}
                              {action.label}
                            </StyledCardButton>
                          ))}
                        </StyledResponseActions>
                      )}
                    </StyledResponseCard>
                  );
                })}
                <div ref={bottomRef} />
              </StyledThread>
            </StyledConversation>

            <StyledInputRow onSubmit={handleSubmit}>
              <StyledUploadButton
                type="button"
                onClick={handleUploadClick}
                disabled={isInteractionBlocked}
                aria-label={routingAiChat.quickActions.uploadCsv}
                title={routingAiChat.quickActions.uploadCsv}
              >
                <PaperclipIcon size={15} color="currentColor" />
              </StyledUploadButton>
              <StyledInput
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={routingAiChat.placeholder}
                aria-label={routingAiChat.placeholder}
                disabled={isInteractionBlocked}
              />
              <StyledSendButton
                type="submit"
                disabled={!draft.trim() || isInteractionBlocked}
                aria-label={routingAiChat.sendLabel}
              >
                <SendIcon size={16} color="currentColor" />
              </StyledSendButton>
            </StyledInputRow>
          </StyledBody>
        </StyledPanel>
      )}
    </>
  );
};

export default withTranslation(RoutingAIChat);
